import { getMinutes } from 'date-fns';

import { QUEUE_DEPTH_SAMPLING_INTERVAL_SECONDS } from '../../../../../../domain/queue-depth';
import LINKS from '../../../configs/links';
import { EventListQueryParams } from '../../../typings/EventList.interface';
import { RequestListQueryParams } from '../../../typings/RequestList.interface';
import { roundNumber, stringifyQuery } from '../../../utils';
import { RelativeDate } from '../../../utils/date';
import { Granularity, MetricModel, QueryParams } from '../../../utils/formatMetricsQuery';
import getFormattedDuration from '../../../utils/getFormattedDuration';
import { DateRange } from '../../scenes/DashboardScene/Metrics/MetricDatePicker';

export type MetricType = 'count' | 'percent' | 'rate' | 'average' | 'max';

export type MaxMetricTotalStrategy = 'latest_max' | 'sum_latest' | 'sum_highest_when_multiple';

export type OrderBy = 'total' | 'highest' | 'average';

export const order_by_by_metric_type: { [key in MetricType]: OrderBy } = {
  count: 'total',
  percent: 'highest',
  rate: 'average',
  average: 'average',
  max: 'highest',
};

export type TimeUnit = {
  value: number; // Length of the time period
  unit: 'days' | 'hours' | 'minutes' | 'seconds'; // Unit of the time period, code only supports hours for now
  sampling: {
    priority_display: [(date: Date) => number, number[]];
    date_format: string; // The date-fns format syntax
    value: number; // Sample length
    granularity: Granularity; // Sample granularity
  };
};

export const metric_time_units: Record<string, TimeUnit> = {
  '1h': {
    value: 1,
    unit: 'hours',
    sampling: {
      value: 1,
      granularity: 'minute',
      date_format: 'h:mm a',
      priority_display: [getMinutes, [15, 30, 45, 0]],
    },
  },
  '6h': {
    value: 6,
    unit: 'hours',
    sampling: {
      value: 5,
      granularity: 'minute',
      date_format: 'h:mm a',
      priority_display: [getMinutes, [15, 30, 45, 0]],
    },
  },
  '24h': {
    value: 24,
    unit: 'hours',
    sampling: {
      value: 30,
      granularity: 'minute',
      date_format: 'h:mm a',
      priority_display: [getMinutes, [30, 0]],
    },
  },
};

export const formatRateMetricValue = (
  value: number | string,
  rate_as?: 'second' | 'minute',
): string => {
  const numeric_value = Number(value);
  const precision = rate_as && Math.abs(numeric_value) > 10 ? 0 : 1;

  return roundNumber(numeric_value, precision).toLocaleString();
};

export const default_data_formatter: Record<
  MetricType,
  (v: number | string, rate_as?: 'second' | 'minute') => string
> = {
  count: (v: number) => roundNumber(v).toLocaleString(),
  percent: (v: number) => `${roundNumber(v)}%`,
  rate: (v: number | string, rate_as) => formatRateMetricValue(v, rate_as),
  average: (v: number) => `${roundNumber(v).toLocaleString()}`,
  max: (v) => (typeof v === 'string' ? v : `${roundNumber(v).toLocaleString()}`),
};

export const charts_metric_time_units = Object.keys(metric_time_units).map((key) => ({
  key: key,
  label: key,
}));

export interface EventMetric {
  dimensions?: ('webhook_id' | 'destination_id' | 'source_id' | 'delivery_group' | 'status')[];
  filters?: {
    webhook_id?: string[];
    destination_id?: string[];
    source_id?: string[];
    delivery_group?: (string | null)[] | null;
  };
}

export interface RequestMetric {
  dimensions?: ['source_id'];
  filters?: {
    source_id?: string[];
  };
}

export interface AttemptMetric {
  dimensions?: ('destination_id' | 'delivery_group')[];
  filters?: {
    destination_id?: string[];
    delivery_group?: (string | null)[] | null;
  };
}

export type MetricOptions = AttemptMetric | RequestMetric | EventMetric;

export type MetricName = keyof typeof metric_definitions;

export const metric_definitions: Record<
  string,
  {
    type: MetricType;
    chart_type?: 'bar' | 'line';
    max_metric_total_strategy?: MaxMetricTotalStrategy;
    resource: 'destination' | 'source' | 'webhook';
    title: string;
    description?: string;
    fill_missing_after_zero?: boolean;
    sampling_interval_seconds?: number;
    description_cta?: {
      label: string;
      to: string;
    };
    tooltip_label: string;
    getQueryParams: (
      options: MetricOptions,
      granularity?: Granularity,
    ) => { model: MetricModel; params: QueryParams };
    buildExploreLink?: (
      options: {
        date?: RelativeDate | DateRange;
      } & MetricOptions,
    ) => string;
    valueToNumber: (v: string) => number;
    formatDataForDisplay?: (v: string | number) => string;
    formatTableTooltipData?: (
      value: number | string,
      optional_data?: {
        resource?: unknown;
        rate_limit: number;
      },
    ) => { label: string; value: string }[];
  }
> = {
  attempts_delivered_count: {
    type: 'count',
    resource: 'destination',
    title: 'Delivered attempts',
    description:
      'Measures the number of delivery attempts that reached your destination(s) over time.',
    tooltip_label: 'Count',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: 'delivered_count',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'deliveredAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  attempts_delivered_rate: {
    type: 'rate',
    resource: 'destination',
    title: 'Attempts delivery rate',
    tooltip_label: 'Rate',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: 'delivered_count',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'deliveredAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  attempts_successful_count: {
    type: 'count',
    resource: 'destination',
    title: 'Successful attempts',
    tooltip_label: 'Successful attempts',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: 'successful_count',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  attempts_failed_count: {
    type: 'count',
    resource: 'destination',
    title: 'Failed attempts',
    tooltip_label: 'Failed attempts',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: 'failed_count',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  attempts_error_rate: {
    type: 'percent',
    resource: 'destination',
    title: 'Attempts failure rate',
    description: 'The rate at which delivery attempt errors to this destination are occurring.',
    tooltip_label: 'Attempts failure rate',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: ['error_rate', 'count'],
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'respondedAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  attempts_average_response_time: {
    type: 'average',
    resource: 'destination',
    title: 'Avg. response latency',
    description: 'Average response time from the destination in milliseconds.',
    tooltip_label: 'Response time',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: 'response_latency_avg',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'respondedAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
    formatDataForDisplay: (v: number) => `${Math.round(v).toLocaleString()} ms`,
  },
  attempts_max_response_time: {
    type: 'average',
    resource: 'destination',
    title: 'Max response latency',
    description: 'Max response time from the destination in milliseconds.',
    tooltip_label: 'Response time',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: 'response_latency_max',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'respondedAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
    formatDataForDisplay: (v: number) => `${Math.round(v).toLocaleString()} ms`,
  },
  attempts_95_response_time: {
    type: 'average',
    resource: 'destination',
    title: '95th response latency',
    description: '95th percentile of response times from the destination in milliseconds.',
    tooltip_label: 'Response time',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: 'response_latency_p95',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'respondedAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
    formatDataForDisplay: (v: number) => `${Math.round(v).toLocaleString()} ms`,
  },
  attempts_99_response_time: {
    type: 'average',
    resource: 'destination',
    title: '99th response latency',
    description: '99th percentile of response times from the destination in milliseconds.',
    tooltip_label: 'Response time',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'Attempts',
        params: {
          measure: 'response_latency_p99',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'respondedAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
    formatDataForDisplay: (v: number) => `${Math.round(v).toLocaleString()} ms`,
  },
  // TODO remove?
  events_pending_rate: {
    type: 'rate',
    resource: 'destination',
    title: 'Events pending rate',
    description: 'Rate at which events are being queued to the destination.',
    tooltip_label: 'Events pending rate',
    buildExploreLink: (options) => {
      return `/explore/events?${stringifyQuery({
        ...options,
        status: ['queued'],
      } as EventListQueryParams)}`;
    },
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'EventsPendingTimeseries',
        params: {
          measure: 'count',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'timestamp',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  // TODO: remove?
  events_pending_timeseries_count: {
    type: 'count',
    resource: 'destination',
    title: 'Pending events',
    description: 'The number of events currently pending delivery (with QUEUED status).',
    tooltip_label: 'Pending events',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery({
        ...options,
        status: ['queued'],
      } as EventListQueryParams)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: {
            ...options.filters,
            status: ['QUEUED'], // Filter for events with QUEUED status (must be array)
          },
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_count: {
    type: 'count',
    resource: 'webhook',
    title: 'Events',
    tooltip_label: 'Events',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery(options)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_rate: {
    type: 'rate',
    resource: 'webhook',
    title: 'Events rate',
    tooltip_label: 'Rate',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery(options)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_oldest_pending: {
    type: 'max',
    sampling_interval_seconds: QUEUE_DEPTH_SAMPLING_INTERVAL_SECONDS,
    resource: 'destination',
    title: 'Oldest pending event',
    fill_missing_after_zero: true,
    description:
      'Measures the longest amount of time an event has been in-queue for delivery. In other message queues, this is sometimes referred to as "Max Age."',
    tooltip_label: 'Oldest pending event',
    getQueryParams: (options: AttemptMetric, granularity) => {
      return {
        model: 'QueueDepth',
        params: {
          measure: 'max_age',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'timestamp',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
    formatDataForDisplay: (data: number) =>
      data === 0 ? 'Ø' : getFormattedDuration(data * 60 * 1000),
  },
  events_max_rate: {
    type: 'max',
    resource: 'webhook',
    title: 'Max events rate',
    tooltip_label: 'Rate',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery(options)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          // Server computes max count over any single second within the bucket,
          // returning a value directly comparable to a per-second rate.
          measure: 'max_count_per_second',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_successful_count: {
    type: 'count',
    resource: 'webhook',
    title: 'Successful events',
    tooltip_label: 'Successful events',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery({
        ...options,
        status: ['successful'],
      } as EventListQueryParams)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'successful_count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_failed_count: {
    type: 'count',
    resource: 'webhook',
    title: 'Failed events',
    tooltip_label: 'Failed events',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery({
        ...options,
        status: ['failed'],
      } as EventListQueryParams)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'failed_count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_pending_count: {
    type: 'max',
    sampling_interval_seconds: QUEUE_DEPTH_SAMPLING_INTERVAL_SECONDS,
    chart_type: 'line',
    max_metric_total_strategy: 'sum_latest',
    resource: 'webhook',
    title: 'Pending events',
    fill_missing_after_zero: true,
    description:
      'Count of events that were in queue (pending) to be delivered to the destination at any given time. In other message queues this is often referred to as "Queue Depth"',
    tooltip_label: 'Pending events',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery({
        ...options,
        status: ['pending'],
      })}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'QueueDepth',
        params: {
          measure: 'max_depth',
          dimensions: options.dimensions,
          time_dimention: 'timestamp',
          filters: options.filters,
          granularity,
        },
      };
    },
    formatTableTooltipData: (value) => {
      return [
        {
          label: 'Pending events',
          value: `${value}`,
        },
      ];
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_paused_count: {
    type: 'count',
    resource: 'webhook',
    title: 'Paused events',
    tooltip_label: 'Paused',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery({
        ...options,
        status: ['paused'],
      } as EventListQueryParams)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'paused_count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    formatTableTooltipData: (value) => {
      return [
        {
          label: 'Paused events',
          value: `${value}`,
        },
      ];
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_delayed_count: {
    type: 'count',
    resource: 'webhook',
    title: 'Delayed events',
    tooltip_label: 'Scheduled',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery({
        ...options,
        status: ['scheduled'],
      } as EventListQueryParams)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'scheduled_count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    formatTableTooltipData: (value) => {
      return [
        {
          label: 'Scheduled events',
          value: `${value}`,
        },
      ];
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_with_scheduled_retry_count: {
    type: 'count',
    resource: 'webhook',
    title: 'Events with scheduled retries',
    description: 'Events that have a future delivery attempt scheduled.',
    tooltip_label: 'Events',
    buildExploreLink: (options) => {
      return `/events?${stringifyQuery({
        ...options,
        scheduled_attempts: 'true',
      } as EventListQueryParams)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'scheduled_retry_count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    formatTableTooltipData: (value) => {
      return [
        {
          label: 'Events with scheduled retry',
          value: `${value}`,
        },
      ];
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_error_rate: {
    type: 'percent',
    resource: 'webhook',
    title: 'Events failure ratio',
    tooltip_label: 'Events failure ratio',
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: ['error_rate', 'count'],
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  events_average_attempts: {
    type: 'average',
    resource: 'webhook',
    title: 'Avg. attempts per event',
    description:
      'Depending on your connection configuration(s), outbound events queued from inbound HTTP requests may make multiple delivery attempts to your destination(s).',
    description_cta: {
      label: 'Attempt docs',
      to: LINKS.product_docs.events,
    },
    tooltip_label: 'Average attempts',
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Events',
        params: {
          measure: 'avg_attempts',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  requests_count: {
    type: 'count',
    resource: 'source',
    title: 'Requests',
    tooltip_label: 'Requests',
    buildExploreLink: (options) => {
      return `/requests?${stringifyQuery(options as RequestListQueryParams)}`;
    },
    getQueryParams: (options: RequestMetric, granularity) => {
      return {
        model: 'Requests',
        params: {
          measure: 'count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  requests_rate: {
    type: 'rate',
    resource: 'source',
    title: 'Requests rate',
    tooltip_label: 'Rate',
    buildExploreLink: (options) => {
      return `/requests?${stringifyQuery(options as RequestListQueryParams)}`;
    },
    getQueryParams: (options: EventMetric, granularity) => {
      return {
        model: 'Requests',
        params: {
          measure: 'count',
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          filters: options.filters,
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  requests_accepted_count: {
    type: 'count',
    resource: 'source',
    title: 'Accepted requests',
    tooltip_label: 'Accepted requests',
    buildExploreLink: (options) => {
      return `/requests?${stringifyQuery({
        ...options,
        status: 'accepted',
      } as RequestListQueryParams)}`;
    },
    getQueryParams: (options: RequestMetric, granularity) => {
      return {
        model: 'Requests',
        params: {
          measure: 'accepted_count',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  requests_rejected_count: {
    type: 'count',
    resource: 'source',
    title: 'Rejected requests',
    tooltip_label: 'Rejected requests',
    buildExploreLink: (options) => {
      return `/requests?${stringifyQuery({
        ...options,
        status: 'rejected',
      } as RequestListQueryParams)}`;
    },
    getQueryParams: (options: RequestMetric, granularity) => {
      return {
        model: 'Requests',
        params: {
          measure: 'rejected_count',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  requests_average_event: {
    type: 'average',
    resource: 'source',
    title: 'Avg. events per request',
    description:
      'Depending on your connection configuration(s), individual HTTP requests may result in multiple outbound events to your destinations.',
    description_cta: {
      label: 'Event docs',
      to: LINKS.product_docs.events,
    },
    tooltip_label: 'Average events',
    getQueryParams: (options: RequestMetric, granularity) => {
      return {
        model: 'Requests',
        params: {
          measure: 'avg_events_per_request',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  requests_average_ignored: {
    type: 'average',
    resource: 'source',
    title: 'Avg. ignored events per request',
    description:
      'When HTTP requests reach Hookdeck, they are typically added to an outbound queue to your destination(s). However, when deterministic routing logic is applied to your connections — e.g., filters — Hookdeck may "ignore" requests that would otherwise be added to your queue.',
    description_cta: {
      label: 'Event docs',
      to: LINKS.product_docs.events,
    },
    tooltip_label: 'Average ignored events',
    getQueryParams: (options: RequestMetric, granularity) => {
      return {
        model: 'Requests',
        params: {
          measure: 'avg_ignored_per_request',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
  requests_discarded_count: {
    type: 'count',
    resource: 'source',
    title: 'Discarded requests',
    description:
      'When inbound HTTP requests to Hookdeck fail to be added to the outbound queue as events, they are considered discarded.',
    tooltip_label: 'Discarded requests',
    buildExploreLink: (options) => {
      return `/requests?${stringifyQuery({
        ...options,
        events_count: {
          min: '0',
          max: '0',
        },
      } as RequestListQueryParams)}`;
    },
    getQueryParams: (options: RequestMetric, granularity) => {
      return {
        model: 'Requests',
        params: {
          measure: 'discarded_count',
          filters: options.filters,
          dimensions: options.dimensions,
          time_dimention: 'createdAt',
          granularity,
        },
      };
    },
    valueToNumber: (v) => parseInt(v),
  },
};
